/**
 * useSmartApiClient Hook
 *
 * Selects the appropriate API client based on route context or session source.
 * Both ApiSubjectManagementAi and ApiReportsChatbot now point to the same
 * unified reports-ai endpoint, but are kept separate for interface compatibility.
 */

import { useMemo } from 'react';
import ApiSubjectManagementAi from '../api/subjectManagementAi';
import ApiReportsChatbot from '../api/reports-chatbot';
import { useReportsProgressTracking } from '../modules/reports/components/reports-ai-core/useReportsProgressTracking';

/**
 * Detect API context from current route
 * @param {string} pathname - Current route pathname
 * @returns {Object} API configuration
 */
const detectApiFromRoute = (pathname) => {
  // Subject Management pages
  if (pathname.includes('/subject-management') ||
      pathname.includes('/subject/')) {
    return {
      apiClient: ApiSubjectManagementAi,
      progressHook: useReportsProgressTracking,
      contextType: 'subject-management',
      defaultTitle: 'New Analysis Session',
      purpose: 'subject-management-assistant'
    };
  }

  // All other pages (reports, analytics, audit-trail, etc.)
  return {
    apiClient: ApiReportsChatbot,
    progressHook: useReportsProgressTracking,
    contextType: 'reports',
    defaultTitle: 'New Analysis Session',
    purpose: 'reports-assistant'
  };
};

/**
 * Get API configuration from session source
 * @param {string} source - 'subject-management' or 'reports'
 * @returns {Object} API configuration
 */
const getApiFromSource = (source) => {
  if (source === 'subject-management') {
    return {
      apiClient: ApiSubjectManagementAi,
      progressHook: useReportsProgressTracking,
      contextType: 'subject-management',
      defaultTitle: 'New Analysis Session',
      purpose: 'subject-management-assistant'
    };
  }

  return {
    apiClient: ApiReportsChatbot,
    progressHook: useReportsProgressTracking,
    contextType: 'reports',
    defaultTitle: 'New Analysis Session',
    purpose: 'reports-assistant'
  };
};

/**
 * Custom hook for smart API client selection
 * @param {string} currentRoute - Current pathname
 * @param {Object} activeSession - Currently active session (optional)
 * @returns {Object} API client configuration
 */
const useSmartApiClient = (currentRoute, activeSession = null) => {
  const apiConfig = useMemo(() => {
    // Priority 1: If viewing existing session, use its source API
    if (activeSession && activeSession.source) {
      return getApiFromSource(activeSession.source);
    }

    // Priority 2: Detect from current route
    return detectApiFromRoute(currentRoute);
  }, [currentRoute, activeSession]);

  return apiConfig;
};

export default useSmartApiClient;
